import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CreditCard,
  Calendar,
  Award,
  Check,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Clock,
  Zap,
  Scaling,
  CheckCircle,
} from "lucide-react";
import { getSaasUrl } from "../utils/saasUrl";

const Home = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [expandedCards, setExpandedCards] = useState({});
  const [honeypot, setHoneypot] = useState(""); // Comments honeypot field
  const [website, setWebsite] = useState(""); // Website honeypot field
  const [name, setName] = useState(""); // Name honeypot field
  const [phone, setPhone] = useState(""); // Phone honeypot field
  const [company, setCompany] = useState(""); // Company honeypot field
  const [formStartTime, setFormStartTime] = useState(null);

  useEffect(() => {
    if (window.location.hash === "#pricing") {
      const element = document.getElementById("pricing");
      if (element) {
        element.scrollIntoView({ behavior: "auto" });
      }
    }
  }, []);

  const handleQuickTrial = async (e) => {
    e.preventDefault();
    if (!email) return;

    // Honeypot check - if any field is filled, it's a bot
    if (honeypot || website || name || phone || company) {
      console.log("Bot detected via honeypot");
      // Return fake success to avoid revealing the honeypot
      setMessage("success");
      setEmail("");
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    const formFillTime = formStartTime ? Math.floor((Date.now() - formStartTime) / 1000) : 0;
    
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/trial", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email,
          useCase: "homepage",
          firstName: "",
          lastName: "",
          company: "",
          employees: "",
          comments: honeypot, // Comments honeypot field
          website: website, // Website honeypot field
          honeypot1: name, // Name honeypot field
          honeypot2: phone, // Phone honeypot field
          honeypot3: company, // Company honeypot field
          form_fill_time: formFillTime,
        }),
      });

      if (response.ok) {
        setMessage("success");
        setEmail("");
      } else {
        setMessage("Error sending request. Please try again.");
      }
    } catch (error) {
      setMessage("Error sending request. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-white min-h-[100vh] flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center">
            <h1 className="text-2xl sm:text-4xl md:text-6xl font-bold mb-6">
              <span className="gradient-text">AI-Powered</span>
              <br />
              <span className="text-gray-800">
                Price Monitoring &<br className="sm:hidden" />{" "}
                <br className="hidden sm:block" />
                Dynamic Pricing Software
              </span>
            </h1>
            <p className="text-base sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto px-2">
              Outsmart the competition. Transform your pricing strategy with
              real-time competitor intelligence, dynamic algorithms, and
              comprehensive marketplace monitoring.
            </p>
            <div className="max-w-xl mx-auto mb-8 px-4">
              <form
                onSubmit={handleQuickTrial}
                className="flex flex-col sm:flex-row gap-3"
              >
                {/* Honeypot fields - hidden from users */}
                <input
                  type="text"
                  name="comments"
                  placeholder="Leave this field empty"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
                  tabIndex="-1"
                  autoComplete="off"
                />
                <input
                  type="url"
                  name="website"
                  placeholder="Your website URL"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
                  tabIndex="-1"
                  autoComplete="off"
                />
                <input
                  type="text"
                  name="name"
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
                  tabIndex="-1"
                  autoComplete="off"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Your phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
                  tabIndex="-1"
                  autoComplete="off"
                />
                <input
                  type="text"
                  name="company"
                  placeholder="Your company name"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
                  tabIndex="-1"
                  autoComplete="off"
                />
                <input
                  type="email"
                  placeholder="Enter your email for free trial"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => !formStartTime && setFormStartTime(Date.now())}
                  className="flex-1 px-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg outline-none"
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-6 py-4 whitespace-nowrap text-base w-full sm:w-auto"
                >
                  {isSubmitting ? "Sending..." : "Try for Free"}
                </button>
              </form>
              {message && (
                <div className="mt-2 text-sm text-center text-green-600 flex items-center justify-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  <span>Request sent successfully!</span>
                </div>
              )}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mt-6 text-sm text-gray-600 px-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-indigo-600" />
                  <span>No Credit Card Required</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-indigo-600" />
                  <span>30 Days Free Trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-indigo-600" />
                  <span>Dedicated Success Coach</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50 py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Full-Spectrum Price Intelligence
            </h2>
            <p className="text-xl text-gray-600">
              Monitor, React, and Win Across Every Sales Channel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link
              to="/dynamic-pricing"
              className="card group ai-glow cursor-pointer"
            >
              <div className="relative z-10">
                <div className="w-15 h-15 flex items-center justify-center mb-4 mx-auto">
                  <img
                    src="/images/logos/monitoring.png"
                    alt="Monitoring"
                    className="h-20 w-20"
                  />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-center">
                  Dynamic Pricing
                </h3>
                <p className="text-gray-600 text-center">
                  AI-driven pricing optimization that adapts to market
                  conditions in real-time. This ensures you always set the
                  optimal price point to maximize conversions and win the sale.
                </p>
              </div>
            </Link>

            <Link
              to="/marketplace-monitoring"
              className="card group ai-glow cursor-pointer"
            >
              <div className="relative z-10">
                <div className="w-20 h-20 flex items-center justify-center gap-2 mb-4 mx-auto">
                  <img
                    src="/images/logos/google-logo-.png"
                    alt="Google"
                    className="h-17 w-17"
                  />
                  <img
                    src="/images/logos/amazon-logo-.png"
                    alt="Amazon"
                    className="h-20 w-20"
                  />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-center">
                  Marketplace Monitoring
                </h3>
                <p className="text-gray-600 text-center">
                  Track competitor prices across Google, Amazon, and major
                  marketplaces. Gain complete, real-time price intelligence on
                  your entire product catalog to inform every strategic move.
                </p>
              </div>
            </Link>

            <Link
              to="/map-alerts"
              className="card group ai-glow cursor-pointer"
            >
              <div className="relative z-10">
                <div className="w-15 h-15 flex items-center justify-center mb-4 mx-auto">
                  <img
                    src="/images/logos/warning.png"
                    alt="Map Alerts"
                    className="h-20 w-20"
                  />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-center">
                  MAP Alerts
                </h3>
                <p className="text-gray-600 text-center">
                  Minimum Advertised Price compliance monitoring and
                  enforcement. We safeguard your brand's market value and your
                  retailer's profits by eliminating unauthorized pricing drops.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Why Choose <span className="gradient-text">StrikeAPrice</span>?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card ai-glow p-8">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">
                    Recover Your Revenue Instantly
                  </h3>
                  <p className="text-gray-600">
                    Stop letting profits slip through the cracks of static
                    pricing. Our dynamic engine identifies true market value in
                    real-time by monitoring competitor moves and demand trends.
                    Capture hidden margins and turn market volatility into a
                    competitive advantage the moment you activate—no more
                    leaving money on the table.
                  </p>
                </div>
              </div>
            </div>
            <div className="card ai-glow p-8">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center flex-shrink-0">
                  <Clock className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">
                    Reclaim 20+ Hours Every Week
                  </h3>
                  <p className="text-gray-600">
                    Eliminate the grind of manual price tracking. Our automated
                    intelligence monitors competitors and marketplaces in
                    real-time, delivering instant alerts and strategic insights.
                    Free your team from the prison of spreadsheets and redirect
                    your energy toward high-level strategy while our system
                    handles the data-heavy execution.
                  </p>
                </div>
              </div>
            </div>
            <div className="card ai-glow p-8">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center flex-shrink-0">
                  <Zap className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">
                    Outpace Your Competition Instantly
                  </h3>
                  <p className="text-gray-600">
                    Eliminate the delay between market shifts and your response.
                    Our automated intelligence executes pricing rules in
                    minutes, not days, allowing you to capitalize on competitor
                    moves and demand spikes the moment they happen. Stay ahead
                    of price wars and secure market opportunities while your
                    competition is still analyzing the data.
                  </p>
                </div>
              </div>
            </div>
            <div className="card ai-glow p-8">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center flex-shrink-0">
                  <Scaling className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">
                    Scale Without Friction
                  </h3>
                  <p className="text-gray-600">
                    Eliminate the operational bottlenecks of manual growth. Our
                    solution provides a seamless, automated infrastructure that
                    scales alongside your business—handling thousands of price
                    points across multiple channels without added headcount.
                    Transition from reactive management to a self-optimizing
                    ecosystem that maintains peak performance, no matter how
                    large your catalog or market grows.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customers Section */}
      <section className="py-12 sm:py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
              Trusted by Leading Brands
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Join leading companies that trust StrikeAPrice to optimize their
              pricing strategy
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 items-center justify-items-center">
            <div className="flex items-center justify-center">
              <img
                src="/images/logos/clients/flipkart-logo.png"
                alt="Flipkart"
                className="h-12 max-w-full opacity-60 hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="flex items-center justify-center">
              <img
                src="/images/logos/clients/sooq-logo.png"
                alt="Sooq"
                className="h-12 max-w-full opacity-60 hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="flex items-center justify-center">
              <img
                src="/images/logos/clients/tiger-global-logo.png"
                alt="Tiger Global"
                className="h-12 max-w-full opacity-60 hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6">
              <span className="gradient-text">Simple, Transparent</span> Pricing
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8 px-2">
              Choose the perfect plan for your business. All plans include a
              30-day free trial.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-7xl mx-auto overflow-x-auto pt-8 pb-8">
              <div className="card relative flex flex-col min-w-[280px]">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Starter</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold">$79</span>
                    <span className="text-gray-600">/month</span>
                  </div>
                  <p className="text-gray-600">
                    Perfect for small businesses getting started
                  </p>
                </div>
                <div className={`overflow-hidden transition-all duration-300`}>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-left">
                        Up to 200 products monitored
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        5 competitors per product
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Daily Price Monitoring
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Unlimited Pricing Rules
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Automated Email Reports
                      </span>
                    </li>
                    {expandedCards.starter && (
                      <>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Onboarding Support
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">Email support</span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
                <button
                  onClick={() =>
                    setExpandedCards((prev) => ({
                      ...prev,
                      starter: !prev.starter,
                    }))
                  }
                  className="flex items-center justify-center text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium"
                >
                  {expandedCards.starter ? (
                    <>
                      Show Less <ChevronUp className="ml-1 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Show More <ChevronDown className="ml-1 h-4 w-4" />
                    </>
                  )}
                </button>
                <div className="mt-auto">
                  <a
                    href={getSaasUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200 transition-colors"
                  >
                    Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>

              <div className="card relative flex flex-col ring-2 ring-orange-500 min-w-[280px]">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Growth</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold">$179</span>
                    <span className="text-gray-600">/month</span>
                  </div>
                  <p className="text-gray-600">
                    For growing businesses scaling their catalog
                  </p>
                </div>
                <div className={`overflow-hidden transition-all duration-300`}>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-left">
                        Up to 1,000 products monitored
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        10 competitors per product
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Daily Price Monitoring
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Unlimited Pricing Rules
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Customised reports</span>
                    </li>
                    {expandedCards.growth && (
                      <>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Automated Email Reports
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Dedicated Support Engineer
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Onboarding Support
                          </span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
                <button
                  onClick={() =>
                    setExpandedCards((prev) => ({
                      ...prev,
                      growth: !prev.growth,
                    }))
                  }
                  className="flex items-center justify-center text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium"
                >
                  {expandedCards.growth ? (
                    <>
                      Show Less <ChevronUp className="ml-1 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Show More <ChevronDown className="ml-1 h-4 w-4" />
                    </>
                  )}
                </button>
                <div className="mt-auto">
                  <a
                    href={getSaasUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold btn-primary"
                  >
                    Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>

              <div className="card relative flex flex-col min-w-[280px]">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Professional</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold">$399</span>
                    <span className="text-gray-600">/month</span>
                  </div>
                  <p className="text-gray-600">
                    Advanced automation for established retailers
                  </p>
                </div>
                <div className={`overflow-hidden transition-all duration-300`}>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-left">
                        Up to 5,000 products monitored
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        15 competitors per product
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Marketplace monitoring*
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Multiple Price Updates
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Unlimited Pricing Rules
                      </span>
                    </li>
                    {expandedCards.professional && (
                      <>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Customised & Automated Reports
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Dedicated Support Engineer
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Onboarding Support
                          </span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
                <button
                  onClick={() =>
                    setExpandedCards((prev) => ({
                      ...prev,
                      professional: !prev.professional,
                    }))
                  }
                  className="flex items-center justify-center text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium"
                >
                  {expandedCards.professional ? (
                    <>
                      Show Less <ChevronUp className="ml-1 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Show More <ChevronDown className="ml-1 h-4 w-4" />
                    </>
                  )}
                </button>
                <div className="mt-auto">
                  <a
                    href={getSaasUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200 transition-colors"
                  >
                    Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>

              <div className="card relative flex flex-col min-w-[280px]">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Enterprise</h3>
                  <div className="mb-2">
                    <span className="text-3xl font-bold">Custom</span>
                  </div>
                  <p className="text-gray-600">
                    Tailored solutions for large organizations
                  </p>
                </div>
                <div className={`overflow-hidden transition-all duration-300`}>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-left">
                        Unlimited products monitored
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Unlimited competitors
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Marketplace Monitoring*
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Real-time updates*</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        Customised & Automated reports
                      </span>
                    </li>
                    {expandedCards.enterprise && (
                      <>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Dedicated account manager
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Phone + priority support
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">
                            Onboarding Support
                          </span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
                <button
                  onClick={() =>
                    setExpandedCards((prev) => ({
                      ...prev,
                      enterprise: !prev.enterprise,
                    }))
                  }
                  className="flex items-center justify-center text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium"
                >
                  {expandedCards.enterprise ? (
                    <>
                      Show Less <ChevronUp className="ml-1 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Show More <ChevronDown className="ml-1 h-4 w-4" />
                    </>
                  )}
                </button>
                <div className="mt-auto">
                  <Link
                    to="/contact"
                    className="w-full inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200 transition-colors"
                  >
                    Contact Sales <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="text-center mt-16">
              <p className="text-gray-600 mb-4">
                Need a custom solution? We're here to help.
              </p>
              <Link to="/contact" className="btn-secondary">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="gradient-bg rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
            <p className="mb-6">
              Empower your team with a proven strategy. Many businesses use our
              tools to eliminate manual work and achieve optimal pricing
              efficiency.
            </p>
            <a
              href={getSaasUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center"
            >
              Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
